import { useState, useEffect, createContext, PropsWithChildren } from "react";

import { apps } from "./Apps";
import { config } from "./Config";

const gettextJs = (window as any).i18n();

const defaultLocale = (window as any).reeExternalConfig.defaultLocale;

interface TranslationsItem {
  languages_code: string;
}

interface TextItem {
  key: string;
  value: string;
}

interface TranslationsContextObject {
  locale: string;
  changeLocale: (newLocale: string) => Promise<void>;
  _: (msgid: string, ...args: any[]) => string;
  dynTr: <T extends TranslationsItem>(
    translations: Array<T>,
    valueFn: (it: T) => string
  ) => string;
  gTxt: (key: string) => string;
}

export const Trans = createContext<TranslationsContextObject>({
  locale: "xx_XX",
  changeLocale: async (newLocale) => {},
  _: (str) => str,
  dynTr: (translations, valueFn) =>
    dynStr(translations, valueFn, "en-US") ?? "",
  gTxt: (key) => "",
});

async function loadLanguage(locale: string): Promise<void> {
  if (locale !== "en_US") {
    gettextJs.loadJSON(
      await fetch(`translations/${locale}.po.json?${Date.now()}`).then(
        (response) => response.json()
      ),
      "messages"
    );
  }
  gettextJs.setLocale(locale);
}

interface TranslationsProps {}
export default function Translations(
  props: PropsWithChildren<TranslationsProps>
) {
  const [locale, setLocale] = useState<string>(defaultLocale);
  const [texts, setTexts] = useState<TextItem[]>([]);
  const [textsDefault, setTextsDefault] = useState<TextItem[]>([]);
  useEffect(() => {
    loadLanguage(defaultLocale);
  }, []);
  useEffect(() => {
    (async () => {
      const app = apps.find((it) => config.spaceId === it.link);
      if (app === undefined) {
        throw Error();
      }
      if (app !== undefined && app.spaceId !== undefined) {
        const textsData: { data: TextItem[] } = await fetch(
          `${config.apiUrl}/items/texts?filter={"space":{"_eq":${
            app.spaceId
          }},"lang":{"_eq":"${locale.replace("_", "-")}"}}&fields=key,value`
        ).then((response) => response.json());
        setTexts(textsData.data);
        if (locale === "en_US") {
          setTextsDefault(textsData.data);
        } else {
          const textsDataEnUs: { data: TextItem[] } = await fetch(
            `${config.apiUrl}/items/texts?filter={"space":{"_eq":${app.spaceId}},"lang":{"_eq":"en-US"}}&fields=key,value`
          ).then((response) => response.json());
          setTextsDefault(textsDataEnUs.data);
        }
      }
    })();
  }, [locale, setTexts, setTextsDefault]);
  return (
    <Trans.Provider
      value={{
        locale: locale,
        changeLocale: async (newLocale: string) => {
          await loadLanguage(newLocale);
          setLocale(newLocale);
        },
        _: (msgid, ...args) => {
          return gettextJs.gettext(msgid, ...args);
        },
        dynTr: (translations, valueFn) =>
          dynStr(translations, valueFn, locale.replace("_", "-")) ?? "",
        gTxt: (key) =>
          texts.find((it) => it.key === key)?.value ??
          textsDefault.find((it) => it.key === key)?.value ??
          "",
      }}
    >
      {props.children}
    </Trans.Provider>
  );
}

function dynStrWithoutFallback<T extends TranslationsItem>(
  translations: Array<T>,
  valueFn: (it: T) => string,
  lang: string
): string | null {
  for (const trans of translations) {
    if (trans.languages_code === lang) {
      const value = valueFn(trans);
      if (value !== undefined && value !== null && typeof value === "string") {
        return value;
      } else {
        return null;
      }
    }
  }
  return null;
}

function dynStr<T extends TranslationsItem>(
  translations: Array<T>,
  valueFn: (it: T) => string,
  lang: string
): string | null {
  const value = dynStrWithoutFallback(translations, valueFn, lang);
  if (lang !== "en-US" && value === null) {
    return dynStrWithoutFallback(translations, valueFn, "en-US");
  } else {
    return value;
  }
}
